import Navbar from "./Navbar";
import Footer from "./Footer";
import "./container.css";
import "./FairPracticeCode.css";

const companyName = () => import.meta.env.VITE_COMPANY_NBFC || "Naman Finlease Pvt. Ltd.";
const registeredAddress = () =>
  import.meta.env.VITE_COMPANY_ADDRESS || "H-144, Sector 63, Noida, Uttar Pradesh, India - 201309";

const FairPracticeCode = () => {
  return (
    <div className="fpc-page">
      <Navbar />

      <div className="fpc-hero">
        <div className="container-8xl">
          <h1 className="fpc-title">Fair Practices Code</h1>
          <p className="fpc-subtitle">
            {companyName()} — Registered Address: {registeredAddress()}
          </p>
          <p className="fpc-subtitle">(Approved and adopted by the Board of Directors)</p>
        </div>
      </div>

      <div className="container-8xl">
        <div className="fpc-card">
          <div className="fpc-section">
            <h2 className="fpc-section-title">1. Preamble</h2>
            <p>
              The Fair Practices Code ("FPC") has been formulated and approved by the Board of
              Directors of {companyName()} ("Company") in accordance with the Master Direction —
              Reserve Bank of India (Non-Banking Financial Company — Responsible Business
              Conduct) Directions, as amended from time to time ("RBI Master Directions").
            </p>
            <p>
              In accordance with the RBI Master Directions, Non-Banking Financial Companies
              ("NBFCs") having customer interface under its brand{" "}
              <strong>{import.meta.env.VITE_COMPANY_NAME}</strong> are required to adopt the
              guidelines on Fair Practice Code prescribed in the RBI Master Directions. The
              Company, being an NBFC having customer interface, has adopted this FPC for its
              lending business. The FPC also seeks to incorporate various disclosure requirements
              prescribed by the guidelines / directions issued by the Reserve Bank of India on
              Digital Lending, as amended from time to time, as applicable to the Company's
              lending business.
            </p>
          </div>

          <div className="fpc-section">
            <h2 className="fpc-section-title">2. Purpose and Its Applicability</h2>
            <p>
              The Company has adopted the FPC with an endeavor to achieve fair and transparent
              practices while dealing with its customers/borrowers. This FPC intends to promote
              good and fair practices by setting minimum standards to be followed while dealing
              with customers, and to increase transparency so that customers can have a better
              understanding of the products/services being offered by the Company.
            </p>
            <p>
              This FPC applies across all aspects of the Company's lending business operations,
              including digital lending, marketing, loan origination, processing, servicing, and
              collection activities. The Company's Board of Directors and management are
              responsible for establishing practices designed to ensure that its operations
              reflect a strong commitment to the FPC, and that all employees are aware of it.
            </p>
          </div>

          <div className="fpc-section">
            <h2 className="fpc-section-title">3. Definitions</h2>
            <ul>
              <li>
                <strong>"Authorised Representative"</strong> means a person other than an
                Advocate duly appointed and authorised in writing to represent a complainant in
                proceedings before the officials of RBI.
              </li>
              <li>
                <strong>"Board"</strong> means the Board of Directors of the Company.
              </li>
              <li>
                <strong>"Complaint"</strong> means any representation made in writing or through
                other modes alleging deficiency in service on the part of the Company, and/or
                seeking relief under the Scheme.
              </li>
              <li>
                <strong>"Company"</strong> means {companyName()}.
              </li>
              <li>
                <strong>"Director"</strong> means an individual Director or any of the Directors
                on the Board of the Company.
              </li>
              <li>
                <strong>"FPC"</strong> means the Fair Practices Code.
              </li>
              <li>
                <strong>"RBI"</strong> means the Reserve Bank of India.
              </li>
            </ul>
          </div>

          <div className="fpc-section">
            <h2 className="fpc-section-title">4. Norms Applicable to All Loan Products of the Company</h2>

            <h3 className="fpc-section-subtitle">(i) Applications for Loans and Their Processing</h3>
            <ol>
              <li>All loan products of the Company shall be as per the Loan Policy adopted by the Company.</li>
              <li>
                All communications to the customer by the Company shall be in English as it is
                commonly understood by all customers of the Company. If a customer explicitly
                requests communication in another language, the Company will acknowledge this
                preference, and all subsequent communications with that customer will be in the
                requested language.
              </li>
              <li>
                As part of the process and to ensure transparency, the Company, at the application
                stage, shall provide all necessary information — including processing
                fees/charges (if any), non-refundable fees in case of rejection of the loan
                proposal, pre-payment options, etc. — which affect the interest of the customer,
                so that a meaningful comparison with the terms offered by other NBFCs can be made
                and an informed decision taken.
              </li>
              <li>The Company shall provide acknowledgment for receipt of loan applications along with the timeframe within which the application would be disposed of.</li>
              <li>The Company, at the loan application stage, shall indicate all documents required to be submitted along with the application form.</li>
              <li>On exercise of choice, the customer would be given relevant information about the loan product of their choice.</li>
              <li>
                The customer would be informed about the status of their application as and when
                required. The Company shall explain the entire loan process and journey till
                sanction and disbursement, including timelines for the ordinary course of
                business.
              </li>
            </ol>

            <h3 className="fpc-section-subtitle">(ii) Loan Appraisal, Terms and Conditions</h3>
            <ol>
              <li>The Company shall scrutinize the information submitted by the customer and, if additional data is required, shall seek it promptly to facilitate expeditious disposal of the loan application.</li>
              <li>
                The Company shall convey key terms and conditions of the proposed loan in writing
                (in English as understood by all customers), by means of a sanction letter or
                other written communication, including: the amount of loan sanctioned along with
                terms and conditions including the annualized rate of interest; details of the
                default interest/overdue charges and the method of their application; overdue
                charges for late repayment expressly mentioned in bold in the loan agreement; no
                capitalisation of overdue charges (no further interest computed on such charges);
                and the customer's explicit acceptance of terms prior to availing the loan.
              </li>
              <li>The quantum and reason for overdue charges shall be clearly disclosed in the loan agreement and Key Fact Statement (KFS), in addition to being displayed on the Company's website under Interest Rates and Service Charges.</li>
              <li>If the Company cannot provide the loan to the applicant, it shall endeavour to convey the reason(s) for rejection.</li>
              <li>The Company shall furnish a copy of the loan agreement, in English as understood by the borrower, along with copies of all relevant enclosures, at the time of sanction/disbursement, duly approved by the customer and countersigned by authorized officials of the Company.</li>
            </ol>

            <h3 className="fpc-section-subtitle">(iii) Terms &amp; Conditions</h3>
            <ol>
              <li>The Company shall give notice, in English as understood by the customer, of any change in terms and conditions including disbursement schedule, interest rates, service charges, or prepayment charges.</li>
              <li>Penal charges for late repayment shall be mentioned in bold letters in the loan agreement.</li>
              <li>Any decision to recall/accelerate payment or performance under the agreement will be in consonance with the loan agreement.</li>
            </ol>

            <h3 className="fpc-section-subtitle">(iv) Other Terms</h3>
            <ol>
              <li>
                If the customer does not adhere to the repayment schedule, a defined process in
                accordance with the laws of the land will be followed for recovery of dues,
                involving reminders and personal visits where needed. In case of default, the
                Company may refer the case to a recovery agent and will inform the customer of
                recovery proceedings being initiated. The Company shall ensure its recovery
                process does not involve harassment, and that all recovery agents engaged undergo
                proper due diligence, background verification, and periodic training on RBI
                guidelines, customer protection, and ethical recovery practices.
              </li>
              <li>The Company shall not charge foreclosure charges/pre-payment penalties on any loans sanctioned to individual borrowers.</li>
              <li>All fees/charges/interest shall be charged as per the interest rate policy adopted by the Company and the terms and conditions applicable to the loan.</li>
              <li>The Company shall consider genuine cases of financial difficulty appropriately; the customer should identify any such problem and inform the Company as soon as possible.</li>
              <li>
                All personal information of the customer shall be confidential and not disclosed
                to any third party unless agreed to by the customer in writing. Customer data
                shall be collected, processed, and shared strictly on the basis of explicit
                customer consent and in compliance with applicable RBI directions and data
                protection laws. "Third party" excludes law enforcement agencies, Credit
                Information Bureau, RBI, other banks and financial institutions, and any other
                state, central, or regulatory body. The Company may also reveal customer
                information if compelled by law, if it is in the public interest, or if the
                interest of the Company requires such disclosure.
              </li>
            </ol>

            <h3 className="fpc-section-subtitle">(v) Responsibility of the Board of Directors</h3>
            <p>
              The Company, with the approval of its Board of Directors, has laid down a Grievance
              Redressal Mechanism ("GRM") within the organization, ensuring that all disputes
              arising out of decisions of the Company's functionaries are heard and disposed of at
              least at the next higher level. The Board of Directors shall annually review
              compliance with the FPC and the functioning of the GRM, with a consolidated report
              submitted to the Board every year.
            </p>

            <h3 className="fpc-section-subtitle">(vi) Grievance Redressal Mechanism</h3>
            <p>
              The Company, with the approval of its Board of Directors, shall appoint a Grievance
              Redressal Officer and a Nodal Officer. Below is the Escalation Matrix approved by
              the Board of Directors of the Company:
            </p>
            <table className="fpc-table">
              <thead>
                <tr>
                  <th>Escalation</th>
                  <th>Contact</th>
                  <th>Next Step</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>First Escalation — Customer Care</td>
                  <td>
                    Phone: {import.meta.env.VITE_COMPANY_PHONE}
                    <br />
                    Email: {import.meta.env.VITE_COMPANY_EMAIL}
                  </td>
                  <td>If unresolved within 7 days, raise the issue with the Grievance Redressal Officer.</td>
                </tr>
                <tr>
                  <td>Second Escalation — Grievance Redressal Officer</td>
                  <td>
                    Email: {import.meta.env.VITE_BRAND_GRIEVANCE_OFFICER_EMAIL || "grievance@emisaathi.com"}
                    {import.meta.env.VITE_BRAND_GRIEVANCE_OFFICER_PHONE && (
                      <>
                        <br />
                        Phone: {import.meta.env.VITE_BRAND_GRIEVANCE_OFFICER_PHONE}
                      </>
                    )}
                  </td>
                  <td>If unresolved within 7 days, raise the issue with the Nodal Officer.</td>
                </tr>
                <tr>
                  <td>Third Escalation — Nodal Officer</td>
                  <td>
                    Email: {import.meta.env.VITE_BRAND_NODAL_OFFICER_EMAIL || "nodal@emisaathi.com"}
                    {import.meta.env.VITE_BRAND_NODAL_OFFICER_PHONE && (
                      <>
                        <br />
                        Phone: {import.meta.env.VITE_BRAND_NODAL_OFFICER_PHONE}
                      </>
                    )}
                  </td>
                  <td>The Nodal Officer ensures disposal of the complaint within 30 days from the date it was raised.</td>
                </tr>
              </tbody>
            </table>
            <p style={{ marginTop: 16 }}>
              All grievances shall be resolved within a maximum period of 30 days from the date of
              receipt, unless otherwise specified by RBI.
            </p>

            <h3 className="fpc-section-subtitle">(vii) Language and Mode of Communicating the FPC</h3>
            <p>
              The Company, in accordance with the Guidelines on FPC and RBI Master Directions,
              shall put in place the FPC in English. The Company shall also make the FPC available
              in regional/vernacular languages for customers who request it.
            </p>
          </div>

          <div className="fpc-section">
            <h2 className="fpc-section-title">
              5. Additional Norms for Digital Lending or Loans Sourced Over a Digital Lending Platform
            </h2>

            <h3 className="fpc-section-subtitle">(i) Norms for Loans Sourced Over Digital Lending Platforms/Apps ("DLAs")</h3>
            <p>
              Where the Company sources borrowers and/or recovers dues over a digital lending
              platform (whether its own or an outsourced platform), the Company shall abide by
              this FPC in letter and spirit, and shall follow the below instructions:
            </p>
            <ol>
              <li>Names of digital lending platforms engaged as agents shall be disclosed on the Company's website.</li>
              <li>Digital lending platforms engaged as agents shall be directed to disclose upfront to the customer the name of the Company on whose behalf they are interacting.</li>
              <li>Immediately after sanction but before execution of the loan agreement, the sanction letter shall be issued to the borrower on the Company's letterhead.</li>
              <li>A copy of the loan agreement, along with copies of all enclosures quoted therein, shall be furnished to all borrowers at the time of sanction/disbursement.</li>
              <li>Effective oversight and monitoring shall be ensured over the digital lending platforms engaged by the Company.</li>
              <li>Adequate efforts shall be made towards creating awareness of the grievance redressal mechanism.</li>
            </ol>

            <h3 className="fpc-section-subtitle">(ii) Norms Followed by the Company with Respect to Digital Lending</h3>
            <p>
              The Company shall comply with the provisions of guidelines/directions issued by the
              Reserve Bank of India on Digital Lending, as amended from time to time, to the
              extent applicable.
            </p>
          </div>

          <div className="fpc-section">
            <h2 className="fpc-section-title">6. Review of the FPC</h2>
            <p>
              The FPC shall be amended or modified with the approval of the Board, and shall be
              reviewed by the Board on an annual basis. Consequent upon any amendments in RBI
              Master Directions or any change in the position of the Company, necessary changes in
              this FPC shall be incorporated and approved by the Board.
            </p>
            <p>
              Notwithstanding anything contained in this FPC, in case of any contradiction between
              a provision of this FPC and any existing legislation, rule, regulation, or law (or
              modification thereof, or enactment of a new applicable law), the provisions of such
              law, legislation, rule, or regulation shall prevail over this FPC.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FairPracticeCode;
